import React from "react";

const DRAG_THRESHOLD_PX = 4;

type DragHandlers = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel" | "onPointerLeave" | "onClickCapture"
>;

interface DragState {
  isDragging: boolean;
  startX: number;
  startScrollLeft: number;
}

const initialDragState: DragState = {
  isDragging: false,
  startX: 0,
  startScrollLeft: 0,
};

export const useHorizontalDragScroll = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragStateRef = React.useRef<DragState>(initialDragState);
  const didDragRef = React.useRef(false);
  const resetDragRef = React.useRef<number | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const clearResetDragTimer = React.useCallback(() => {
    if (resetDragRef.current !== null) {
      window.clearTimeout(resetDragRef.current);
      resetDragRef.current = null;
    }
  }, []);

  const endDrag = React.useCallback(
    () => {
      if (!dragStateRef.current.isDragging) return;

      dragStateRef.current.isDragging = false;
      setIsDragging(false);

      if (didDragRef.current) {
        clearResetDragTimer();
        resetDragRef.current = window.setTimeout(() => {
          didDragRef.current = false;
          resetDragRef.current = null;
        }, 0);
      } else {
        didDragRef.current = false;
      }
    },
    [clearResetDragTimer]
  );

  const onPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;

    const container = containerRef.current;
    if (!container) return;

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
    };

    didDragRef.current = false;
    setIsDragging(true);
  }, []);

  const onPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging || event.pointerType !== "mouse") return;

    const container = containerRef.current;
    if (!container) return;

    const deltaX = event.clientX - dragStateRef.current.startX;
    if (!didDragRef.current && Math.abs(deltaX) <= DRAG_THRESHOLD_PX) {
      return;
    }

    if (!didDragRef.current && Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
      didDragRef.current = true;
    }

    container.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
    event.preventDefault();
  }, []);

  const onPointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== "mouse") return;
      endDrag();
    },
    [endDrag]
  );

  const onPointerCancel = React.useCallback(
    () => {
      endDrag();
    },
    [endDrag]
  );

  const onPointerLeave = React.useCallback(() => {
    endDrag();
  }, [endDrag]);

  const onClickCapture = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!didDragRef.current) return;

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const centerScroll = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const overflowing = container.scrollWidth - container.clientWidth > 1;
    setIsOverflowing((prev) => (prev === overflowing ? prev : overflowing));

    if (!window.matchMedia("(min-width: 769px)").matches) {
      container.scrollLeft = 0;
      return;
    }

    if (!overflowing) {
      container.scrollLeft = 0;
      return;
    }

    const maxScroll = container.scrollWidth - container.clientWidth;
    const rawTarget = maxScroll / 2;
    const paddingLeft = Number.parseFloat(window.getComputedStyle(container).paddingLeft) || 0;

    const chipSnapPoints = Array.from(container.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement)
      .map((child) => {
        const chipLeft = child.offsetLeft - paddingLeft;
        return Math.max(0, Math.min(chipLeft, maxScroll));
      });

    const snapPoints = [0, maxScroll, ...chipSnapPoints];
    const snappedTarget = snapPoints.reduce((closest, point) => {
      return Math.abs(point - rawTarget) < Math.abs(closest - rawTarget) ? point : closest;
    }, rawTarget);

    container.scrollLeft = snappedTarget;
  }, []);

  React.useEffect(() => {
    return () => {
      clearResetDragTimer();
    };
  }, [clearResetDragTimer]);

  React.useEffect(() => {
    const handleResize = () => centerScroll();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [centerScroll]);

  const dragHandlers: DragHandlers = React.useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onClickCapture,
    }),
    [onClickCapture, onPointerCancel, onPointerDown, onPointerLeave, onPointerMove, onPointerUp]
  );

  return {
    containerRef,
    isDragging,
    isOverflowing,
    dragHandlers,
    centerScroll,
  };
};
